import { localToUtcIso, utcIsoToLocal } from '@/lib/delay/time';

/**
 * «+X h» — the TOTAL delay relative to the STD, the number the passenger hears
 * ("demorado 3 h"; brief §1b decision 2) — as the operator types it, and the wall-clock
 * arithmetic that keeps it in sync with an ETD calendar (`datetime-local` values in the
 * departure airport's zone, DST-correct through `lib/delay/time`).
 */

const HOURS_RE = /^\+?\s*(\d{1,3})(?:[.,](\d{1,2})|:([0-5]\d))?\s*h?$/i;
const MINUTES_RE = /^\+?\s*(\d{1,4})\s*m(?:in)?$/i;

/**
 * Whole minutes of a typed delay, or `null` when it is not one: `3` · `3.5` · `3,5` ·
 * `3:30` · `+3 h` (hours), `45 min` (minutes). Zero is not a delay.
 */
export function parseDelayHours(text: string): number | null {
  const s = text.trim();
  const asMinutes = MINUTES_RE.exec(s);
  let total: number;
  if (asMinutes) total = Number(asMinutes[1]);
  else {
    const m = HOURS_RE.exec(s);
    if (!m) return null;
    const extra = m[2] !== undefined ? Math.round(Number(`0.${m[2]}`) * 60) : Number(m[3] ?? 0);
    total = Number(m[1]) * 60 + extra;
  }
  return total > 0 ? total : null;
}

/** Minutes → how «+X h» shows them: `3`, `3:30`, `0:45` (a form the parser reads back). */
export function formatDelayHours(minutes: number): string {
  const m = Math.round(minutes);
  const rest = m % 60;
  return rest ? `${Math.floor(m / 60)}:${String(rest).padStart(2, '0')}` : String(m / 60);
}

/** A `datetime-local` value in `zone` moved by `minutes` (DST-correct); `''` when it is not complete. */
export function shiftWall(local: string, zone: string, minutes: number): string {
  const iso = localToUtcIso(local, zone);
  return iso ? utcIsoToLocal(new Date(Date.parse(iso) + minutes * 60_000).toISOString(), zone) : '';
}

/** ETD − STD in whole minutes (both `datetime-local` in `zone`), or `null` while either is incomplete. */
export function wallDelayMinutes(std: string, etd: string, zone: string): number | null {
  const a = std ? localToUtcIso(std, zone) : null;
  const b = etd ? localToUtcIso(etd, zone) : null;
  return a && b ? Math.round((Date.parse(b) - Date.parse(a)) / 60_000) : null;
}
